import { ref, computed, watch, onMounted } from 'vue'

export type ThemeMode = 'dark' | 'light' | 'system'

// Global state for theme
const themePreference = ref<ThemeMode>('dark')
const systemTheme = ref<'dark' | 'light'>('dark')

// System theme detection
const detectSystemTheme = (): 'dark' | 'light' => {
  if (typeof window === 'undefined') return 'dark'
  
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  return mediaQuery.matches ? 'dark' : 'light'
}

// Setup system theme listener
let mediaQueryListener: MediaQueryList | null = null

const setupSystemThemeListener = () => {
  if (typeof window === 'undefined') return
  
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  
  const handler = (e: MediaQueryListEvent) => {
    systemTheme.value = e.matches ? 'dark' : 'light'
    console.log('🎨 System theme changed to:', systemTheme.value)
  }
  
  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handler)
  } else {
    // Legacy support
    mediaQuery.addListener(handler)
  }
  
  mediaQueryListener = mediaQuery
}

const cleanupSystemThemeListener = () => {
  if (!mediaQueryListener) return
  
  const handler = () => {}
  if (mediaQueryListener.removeEventListener) {
    mediaQueryListener.removeEventListener('change', handler)
  } else {
    mediaQueryListener.removeListener(handler)
  }
}

export function useTheme() {
  // Computed effective theme (resolves 'system' to actual theme)
  const effectiveTheme = computed<'dark' | 'light'>(() => {
    if (themePreference.value === 'system') {
      return systemTheme.value
    }
    return themePreference.value
  })
  
  // Initialize theme from localStorage
  const initializeTheme = () => {
    // Load preference from localStorage
    const savedTheme = localStorage.getItem('appTheme') as ThemeMode | null
    if (savedTheme && ['dark', 'light', 'system'].includes(savedTheme)) {
      themePreference.value = savedTheme
    } else {
      themePreference.value = 'light' // Default to light (FT style)
      localStorage.setItem('appTheme', 'light')
    }
    
    // Detect current system theme
    systemTheme.value = detectSystemTheme()
    
    // Setup listener for system theme changes
    setupSystemThemeListener()
    
    console.log('🎨 Theme initialized:', {
      preference: themePreference.value,
      system: systemTheme.value,
      effective: effectiveTheme.value
    })
  }
  
  // Set theme preference
  const setTheme = (theme: ThemeMode) => {
    themePreference.value = theme
    localStorage.setItem('appTheme', theme)
    console.log('🎨 Theme preference set to:', theme, '(effective:', effectiveTheme.value, ')')
  }
  
  // Get current theme preference
  const getThemePreference = () => themePreference.value
  
  // Get effective theme
  const getEffectiveTheme = () => effectiveTheme.value
  
  return {
    themePreference: computed(() => themePreference.value),
    effectiveTheme,
    systemTheme: computed(() => systemTheme.value),
    setTheme,
    getThemePreference,
    getEffectiveTheme,
    initializeTheme,
    cleanupSystemThemeListener
  }
}

